import React, { FC, useRef, useState } from 'react';
import styled from 'styled-components';
import App from '../../../models/app';
import {
  SectionTitle
} from 'components/v2/Common';
import AppShapedImage from '../AppShapedImage/index';
import Typography from '../Typography';
import { roundEdgeDevices } from '../../../constants';

interface AppCarouselProps {
  app: App;
  screenshotsSectionTitle?: string;
  deviceName?: string;
  appName?: string;
  developerName?: string;
}

const CarouselContainer = styled.ul<{ isDragging: boolean }>`
  display: flex;
  flex-direction: row;
  gap: 16px;
  margin: 0;
  list-style: none;
  overflow-x: scroll;
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: ${props => props.isDragging ? 'none' : 'x mandatory'};
  scroll-behavior: ${props => props.isDragging ? 'auto' : 'smooth'};
  padding: 16px calc(50% - 90px);
  width: 100%;
  box-sizing: border-box;
  touch-action: pan-x;
  cursor: ${props => props.isDragging ? 'grabbing' : 'grab'};
  user-select: none;
  
  /* Hide scrollbar */
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
`;

const CarouselItem = styled.li`
  list-style: none;
  flex: 0 0 180px;
  width: 180px;
  scroll-snap-align: center;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 180px;
  overflow: hidden;
  background: transparent;
`;

const StyledAppShapedImage = styled(AppShapedImage)`
  width: 180px;
  height: 180px;
`;

const AppInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 24px;
  text-align: center;
`;

const AppCarousel: FC<AppCarouselProps> = ({
  app,
  screenshotsSectionTitle,
  deviceName,
  appName,
  developerName,
}) => {
  const banners = app.banners || [];
  const containerRef = useRef<HTMLUListElement>(null);
  
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  if (banners.length === 0) {
    return null;
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!containerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);
  };

  const handleMouseLeaveOrUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !containerRef.current) return;
    e.preventDefault();
    const x = e.pageX - containerRef.current.offsetLeft;
    const walk = (x - startX) * 1.5; // speed factor
    containerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!containerRef.current) return;
    setIsDragging(true);
    const touch = e.touches[0];
    setStartX(touch.pageX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !containerRef.current) return;
    const touch = e.touches[0];
    const x = touch.pageX - containerRef.current.offsetLeft;
    const walk = (x - startX) * 1.5; // speed factor
    containerRef.current.scrollLeft = scrollLeft - walk;
  };

  return (
    <React.Fragment>
      <CarouselContainer
        ref={containerRef}
        isDragging={isDragging}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseLeaveOrUp}
        onMouseUp={handleMouseLeaveOrUp}
        onMouseMove={handleMouseMove}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchMove={handleTouchMove}
        onDragStart={(e) => e.preventDefault()}
      >
        {banners.map((banner, index) => (
          <CarouselItem key={`CarouselItem_${index}`}>
            <StyledAppShapedImage
              photoProperties={{
                src: banner.uri,
                alt: `${app.name} screenshot ${index + 1}`,
                width: banner.width,
                height: banner.height,
                isSquircle: deviceName ? roundEdgeDevices.includes(deviceName.toLowerCase()) : false,
              }}
              width={180}
              height={180}
              borderRadius="24px"
            />
          </CarouselItem>
        ))}
      </CarouselContainer>
      {(appName || developerName) && (
        <AppInfoContainer>
          {appName && (
            <Typography variant="labelLarge" color="var(--sys-color--on-surface)">
              {appName}
            </Typography>
          )}
          {developerName && (
            <Typography variant="bodyMedium" color="var(--sys-color--on-surface-variant)">
              {developerName}
            </Typography>
          )}
        </AppInfoContainer>
      )}
    </React.Fragment>
  );
};

export default AppCarousel;
